import React, { useMemo } from 'react';
import moment from 'moment';
import classNames from 'classnames';

import {
  orderStage,
  instantBookOrderStage,
  instantBookOrderSecondaryStage,
} from 'common/constants/booking';

export default function InstantBookProcessesRecord({
  order,
  showAllProcess,
  hasRejectedRefund,
  t,
  hasStripeRefundedRefund,
  hasNonStripeRefundedRefund,
}) {
  const processes = order.processes;

  const handleNopaymentProcess = processData => {
    const bookingConfirmed = processData.find(
      item => item.currentStage === instantBookOrderStage.BOOKING_CONFIRMED,
    );
    const bookingConfirmedIndex = processData.findIndex(
      item => item.currentStage === instantBookOrderStage.BOOKING_CONFIRMED,
    );

    if (
      order.landlordConfirmedPayment &&
      bookingConfirmed.currentSecondaryStage ===
        instantBookOrderSecondaryStage.PENDING_GUARANTOR_INFO
    ) {
      // 修改payment received的更新时间
      processData[bookingConfirmedIndex] = {
        ...bookingConfirmed,
        updatedAt: order.paidAt,
      };

      // 自定义额外的流程
      processData.splice(
        bookingConfirmedIndex + 1,
        0,
        {
          ...bookingConfirmed,
          currentStage: 'pending_payment',
        },
        {
          ...bookingConfirmed,
          currentStage: 'booking_confirmed',
        },
      );
    } else {
      processData.splice(
        bookingConfirmedIndex + 1,
        0,
        {
          ...bookingConfirmed,
          currentStage: 'pending_payment',
        },
        {
          ...bookingConfirmed,
          currentStage: 'booking_confirmed',
        },
      );
    }

    // 针对已付款，并且文案是payment received，更新updatedAt
    if (order.landlordConfirmedPayment) {
      processData.map(item => {
        if (
          item.currentStage === instantBookOrderStage.COMPLETED &&
          item.currentSecondaryStage === instantBookOrderSecondaryStage.NO_NEED_GUARANTOR
        ) {
          item = {
            ...item,
            updatedAt: order.paidAt,
          };
        }
      });

      // confirmed流程，如果已付款且没有secondaryStage，没有进行额外的文案处理，会导致存在两个booking confirmed,需要手动删除
      const noNeedCustomBookingConfirmedIndex = processData.findIndex(
        item =>
          item.currentStage === instantBookOrderStage.BOOKING_CONFIRMED &&
          !item.currentSecondaryStage,
      );
      if (noNeedCustomBookingConfirmedIndex > -1) {
        processData.splice(noNeedCustomBookingConfirmedIndex, 1);
      }
    }

    if (!order.landlordConfirmedPayment) {
      // confirmed流程，如果未付款，没有进行额外的文案处理，会导致存在两个booking confirmed,需要手动删除
      const bookingConfirmedIndex = processData.findIndex(
        item => item.currentStage === instantBookOrderStage.BOOKING_CONFIRMED,
      );
      if (bookingConfirmedIndex > -1) {
        processData.splice(bookingConfirmedIndex, 1);
      }

      // completed流程，如果未付款，没有进行额外的文案处理，会导致存在两个pending payment,需要手动删除
      const pendingPaymentIndex = processData.findIndex(
        item =>
          item.currentStage === instantBookOrderStage.COMPLETED &&
          item.currentSecondaryStage === instantBookOrderSecondaryStage.NO_NEED_GUARANTOR,
      );
      if (pendingPaymentIndex > -1) {
        processData.splice(pendingPaymentIndex, 1);
      }
    }

    return processData;
  };

  const newProcess = useMemo(() => {
    let tempProcess = [...processes];
    if (hasRejectedRefund) {
      const rejectInfo = Object.assign({}, processes[processes?.length - 1]);
      rejectInfo.currentSecondaryStage = 'Rejected';
      tempProcess?.splice(processes?.length - 1, 0, rejectInfo);
    }

    tempProcess.sort((a, b) => (moment(a.updatedAt).isBefore(b.updatedAt) ? 1 : -1));
    if (!order?.paymentOnline) {
      tempProcess = handleNopaymentProcess(tempProcess);
    }

    if (!showAllProcess) {
      tempProcess = tempProcess.slice(0, 2);
      return tempProcess;
    }

    return tempProcess;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.processes, showAllProcess]);

  const isProcessFailed = process => {
    const { currentStage } = process;

    return !![
      orderStage.LOST,
      orderStage.CANCELLED,
      orderStage.AFTER_SALES_INTERVENTION,
      orderStage.REFUNDING,
    ].includes(currentStage);
  };

  const handleInstantBookProcessDesc = process => {
    const studentName = `${order.student.firstName} ${order.student.lastName}`;
    const landLordName = order.landlord?.name;
    const isNopayment = !order?.paymentOnline;
    const normalProcess = isNopayment
      ? [
          instantBookOrderStage.FILLING_PERSONAL_INFO,
          instantBookOrderStage.SIGNING_AGREEMENT,
          instantBookOrderStage.SELECTING_PAYMENT_PLAN,
        ]
      : [
          instantBookOrderStage.FILLING_PERSONAL_INFO,
          instantBookOrderStage.SIGNING_AGREEMENT,
          instantBookOrderStage.MAKING_PAYMENT,
        ];

    if (normalProcess.includes(process.currentStage)) {
      return t(`landlord.longtail_booking.process.record.${process.currentStage?.toLowerCase()}`, {
        modifier_name: process.modifierName,
      });
    } else if (
      [instantBookOrderStage.CANCELLED, instantBookOrderStage.REFUNDING].includes(
        process.currentStage,
      )
    ) {
      if (
        process.currentStage === instantBookOrderStage.CANCELLED &&
        process.currentSecondaryStage === instantBookOrderSecondaryStage.CANCEL_BY_STUDENT
      ) {
        return t('landlord.longtail_booking.process.stage.reserve_room.cancel_by_student', {
          modifier_name: process.modifierName,
        });
      }
      return t(
        `landlord.instant_booking.process.record.${process.currentStage?.toLowerCase()}${
          process?.currentSecondaryStage ? `.${process.currentSecondaryStage?.toLowerCase()}` : ''
        }`,
        { modifier_name: process.modifierName },
      );
    }

    if (isNopayment) {
      if (process.currentStage === 'booking_confirmed') {
        return t('landlord.longtail_booking.process.record.booking_confirmed.no_need_guarantor');
      } else if (process.currentStage === 'pending_payment') {
        return t('landlord.instant_booking.process.record.no_payment.payment_received', {
          modifier_name: studentName,
        });
      } else if (
        process.currentStage === instantBookOrderStage.COMPLETED &&
        process?.currentSecondaryStage === instantBookOrderSecondaryStage.NO_NEED_GUARANTOR &&
        !order?.landlordConfirmedPayment
      ) {
        return t('landlord.instant_booking.process.record.no_payment.payment_received', {
          modifier_name: studentName,
        });
      } else if (
        order?.landlordConfirmedPayment &&
        process.currentStage === instantBookOrderStage.BOOKING_CONFIRMED &&
        process?.currentSecondaryStage === instantBookOrderSecondaryStage.PENDING_GUARANTOR_INFO
      ) {
        return t(
          'landlord.instant_booking.process.record.no_payment.payment_received_pending_guarantor',
          {
            landlord_name: landLordName,
          },
        );
      } else if (
        order?.landlordConfirmedPayment &&
        process.currentStage === instantBookOrderStage.COMPLETED &&
        process?.currentSecondaryStage === instantBookOrderSecondaryStage.NO_NEED_GUARANTOR
      ) {
        return t(
          'landlord.instant_booking.process.record.no_payment.payment_received_no_need_guarantor',
          {
            landlord_name: landLordName,
          },
        );
      } else if (
        !order?.landlordConfirmedPayment &&
        process.currentStage === instantBookOrderStage.COMPLETED &&
        process?.currentSecondaryStage === instantBookOrderSecondaryStage.GUARANTOR_PROVIDED
      ) {
        return t(
          'landlord.instant_booking.process.record.no_payment.payment_received_guarantor_provided',
          {
            modifier_name: studentName,
          },
        );
      } else if (
        order?.landlordConfirmedPayment &&
        process.currentStage === instantBookOrderStage.COMPLETED &&
        process?.currentSecondaryStage === instantBookOrderSecondaryStage.GUARANTOR_PROVIDED
      ) {
        return t('landlord.longtail_booking.process.record.completed');
      } else if (process.currentStage === instantBookOrderStage.BOOKING_CONFIRMED) {
        return t(
          `landlord.longtail_booking.process.record.${process.currentStage?.toLowerCase()}`,
          {
            modifier_name: process.modifierName,
          },
        );
      }
    } else {
      return t(
        `landlord.longtail_booking.process.record.${process.currentStage?.toLowerCase()}${
          process?.currentSecondaryStage ? `.${process.currentSecondaryStage?.toLowerCase()}` : ''
        }`,
        { modifier_name: process.modifierName },
      );
    }
  };

  return (
    <For each="process" of={newProcess} index="key">
      <div className="longtail-booking-details__status" key={key}>
        <div
          className={classNames('longtail-booking-details__process-detail-point', {
            'longtail-booking-details__process-detail-point--failed': isProcessFailed(process),
          })}
        />
        <span className="longtail-booking-details__process-time">
          {moment(process.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
        </span>
        <span className="longtail-booking-details__status-text">
          <Choose>
            <When condition={hasRejectedRefund && process?.currentSecondaryStage === 'Rejected'}>
              {t('landlord.longtail_booking_process.instant_book_refund_reject_desc')}
            </When>
            {/* ReserveRoom  stripe refund*/}
            <When
              condition={
                hasStripeRefundedRefund &&
                process?.currentStage === 'REFUNDING' &&
                process?.currentSecondaryStage === 'PARTIAL_NON_REFUND'
              }
            >
              {t('landlord.longtail_booking_process.refund_refuned_desc')}
            </When>
            {/* ReserveRoom  非stripe refund*/}
            <When
              condition={
                hasNonStripeRefundedRefund &&
                process?.currentStage === 'REFUNDING' &&
                process?.currentSecondaryStage === 'PARTIAL_NON_REFUND'
              }
            >
              {t('landlord.instant_booking.process.record.refunding.partial_non_refund')}
            </When>
            <Otherwise>{handleInstantBookProcessDesc(process)}</Otherwise>
          </Choose>
        </span>
      </div>
    </For>
  );
}
